import { EnumTagGroupType } from "gql/generated";
import { ListFilterScreenProps } from "./useFilters";
import { Center } from "styled-system/jsx";
import { ExpandableChecklist, Spinner } from "@ttc3k/trekker";
import { EmptyState } from "shared/components";
import { useTranslation } from "react-i18next";
import { getMultilingualString } from "shared/utils";
import _ from "lodash";
import { useBuildListContext } from "../BuildListContext";

export const KeywordsFilter = ({
  filters,
  onUpdateFilters,
}: ListFilterScreenProps) => {
  const { t } = useTranslation();

  const { tags, tagsError, tagsLoading } = useBuildListContext();

  const filteredTags = tags.filter(
    (tag) => tag.tagGroup?.type === EnumTagGroupType.Global,
  );

  if (tagsLoading) {
    return (
      <Center h={"full"}>
        <Spinner w={"40px"} h={"40px"} />
      </Center>
    );
  }

  if (tagsError) {
    throw new Error("Error loading tags");
  }

  if (!filteredTags || filteredTags.length < 1) {
    return <EmptyState text={t("core:FILTER.KEYWORD_TAGS.EMPTY")} />;
  }

  const groupedTags = _.groupBy(filteredTags, (tag) => tag.tagGroup?._id);
  const formattedTagData = _.sortBy(
    Object.entries(groupedTags).map(([tagGroupId, tagsInGroup]) => ({
      label: tagsInGroup[0].tagGroup?.name
        ? getMultilingualString(tagsInGroup[0].tagGroup?.name)
        : "",
      value: tagGroupId,
      items: _.sortBy(
        tagsInGroup.map((tag) => ({
          label: getMultilingualString(tag.name),
          value: tag._id as string,
        })),
        (tag) => tag.label.toLowerCase(),
      ),
    })),
    (group) => group.label.toLowerCase(),
  );

  const selectedTags = filters.tags.global;
  const filledTagGroups = filters.filledTagGroups.global;
  // These are the tagGroups with tags already selected beneath it that will be
  // set to open by default
  const defaultOpenTagGroups = Array.from(
    new Set(
      filteredTags
        .filter((tag) => selectedTags.includes(tag._id))
        .map((tag) => tag.tagGroup?._id),
    ),
  ).filter((id) => !filledTagGroups.includes(id));

  const handleTagSelect = (tagIds: string[]) => {
    onUpdateFilters({ tags: { ...filters.tags, global: tagIds } });
  };

  const handleTagGroupSelect = (tagGroupIds: string[]) => {
    onUpdateFilters({
      filledTagGroups: { ...filters.filledTagGroups, global: tagGroupIds },
    });
  };
  return (
    <ExpandableChecklist
      data={formattedTagData}
      categoriesValue={filledTagGroups}
      itemsValue={selectedTags}
      onItemsValueChange={handleTagSelect}
      onCategoriesValueChange={handleTagGroupSelect}
      defaultValue={defaultOpenTagGroups}
      multiple
    />
  );
};
